import React from "react";
import { motion } from "framer-motion";
import {
  Users,
  TrendingUp,
  HandCoins,
  FileText,
  ReceiptText,
  RotateCcw,
  ChevronDown,
} from "lucide-react";
import ReloadButton from "../AssetsComponent/ReloadButton";
import { IOrderValidation } from "../../models/Order.model";
import SearchInput from "../Search/SearchInput";

export type PaymentType =
  | "order"
  | "epc"
  | "commission"
  | "rePayment"
  | "SalesRepresentative"
  | "payment"
  | "invoices";

type NavigationHeaderProps = {
  paymentType: PaymentType;
  setPaymentType: (type: PaymentType) => void;
  view: IOrderValidation;
  setView: (view: IOrderValidation) => void;
  role: string;
  onLogout: () => void;
  onReload: () => void;
  search: string;
  setSearch: (value: string) => void;
};

const NavigationHeader: React.FC<NavigationHeaderProps> = ({
  paymentType,
  setPaymentType,
  view,
  setView,
  role,
  onLogout,
  onReload,
  search,
  setSearch,
}) => {
  const [isNavigationOpen, setIsNavigationOpen] = React.useState(false);
  const filterOptions = [
    { value: "order", label: "Orders Payments", icon: Users },
    { value: "epc", label: "EPC Payments", icon: TrendingUp },
    { value: "commission", label: "Commissions", icon: HandCoins },
    { value: "rePayment", label: "Repayments", icon: RotateCcw },
    {
      value: "SalesRepresentative",
      label: "Sales Representative",
      icon: Users,
    },
    {
      value: "payment",
      label: "Payments",
      icon: ReceiptText,
    },
    { value: "invoices", label: "Invoices", icon: FileText },
  ];
  const activeFilter =
    filterOptions.find((option) => option.value === paymentType) ??
    filterOptions[0];
  const ActiveIcon = activeFilter.icon;

  const statusOptions = [
    { value: IOrderValidation.pending, label: "Pending" },
    { value: IOrderValidation.credited, label: "Credited" },
    { value: IOrderValidation.approved, label: "Approved" },
    { value: IOrderValidation.rejected, label: "Rejected" },
    { value: IOrderValidation.cancelled, label: "Canceled" },
  ];
  const filteredStatusOptions =
    paymentType === "epc" ||
    paymentType === "rePayment" ||
    paymentType === "commission"
      ? statusOptions.filter(
          (option) =>
            option.value === IOrderValidation.pending ||
            option.value === IOrderValidation.approved ||
            option.value === IOrderValidation.rejected,
        )
      : statusOptions;
  return (
    <div className="sticky top-0 z-50 backdrop-blur-xl bg-white/95 border-b border-neutral-200/60">
      <div className="mx-auto px-6 py-5">
        {/* Bottom Row - Filter Tabs */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          {/* Payment Type Filters */}
          <div className="flex items-center justify-between gap-3 lg:justify-start">
            <button
              type="button"
              aria-expanded={isNavigationOpen}
              aria-controls="payment-type-navigation"
              onClick={() => setIsNavigationOpen((open) => !open)}
              className="flex min-w-0 flex-1 items-center justify-between rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm font-medium text-neutral-800 lg:hidden"
            >
              <span className="flex min-w-0 items-center gap-2">
                <ActiveIcon className="h-4 w-4 shrink-0" />
                <span className="truncate">{activeFilter.label}</span>
              </span>
              <ChevronDown
                className={`h-4 w-4 shrink-0 transition-transform ${
                  isNavigationOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            <div
              id="payment-type-navigation"
              className={`grid-cols-2 gap-1 rounded-xl border border-neutral-200/40 bg-neutral-100/60 p-1 backdrop-blur-sm ${
                isNavigationOpen ? "grid" : "hidden"
              } lg:flex lg:items-center lg:gap-0`}
              aria-label="Payment sections"
            >
              {filterOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <motion.button
                    key={option.value}
                    onClick={() => {
                      setView(IOrderValidation.pending);
                      setPaymentType(option.value as PaymentType);
                      setIsNavigationOpen(false);
                    }}
                    className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-all duration-300 lg:w-auto lg:px-4 ${
                      paymentType === option.value
                        ? "bg-white text-neutral-800 shadow-sm ring-1 ring-neutral-200/50 backdrop-blur-sm"
                        : "text-neutral-600 hover:text-neutral-800 hover:bg-white/40"
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{option.label}</span>
                  </motion.button>
                );
              })}
            </div>
            <ReloadButton onReload={onReload} />
          </div>
        </div>
        {/* Status Filters */}
        {paymentType !== "SalesRepresentative" &&
          paymentType !== "payment" &&
          paymentType !== "invoices" && (
            <div className="relative mt-2 flex flex-wrap items-center gap-3">
              <div className="flex flex-wrap items-center gap-1 rounded-xl border border-neutral-200/40 bg-neutral-100/60 p-1 backdrop-blur-sm">
                {filteredStatusOptions.map((option) => (
                  <motion.button
                    key={option.value}
                    onClick={() => setView(option.value as IOrderValidation)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                      !search.trim() && view === option.value
                        ? "bg-white text-neutral-800 shadow-sm ring-1 ring-neutral-200/50 backdrop-blur-sm"
                        : "text-neutral-600 hover:text-neutral-800 hover:bg-white/40"
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span>{option.label}</span>
                  </motion.button>
                ))}
              </div>
            </div>
          )}
        {["epc", "order", "invoices"].includes(paymentType) && (
          <div className="relative mt-2">
            <SearchInput
              placeholder={
                paymentType === "invoices"
                  ? "Search invoice, order, or customer..."
                  : "Search customer name or mobile..."
              }
              onSearch={(term) => setSearch(term)}
              className="w-full rounded-xl border border-neutral-200 bg-white py-2.5 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 lg:w-96"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default NavigationHeader;
